import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import BottomButtonItem from './BottomButtonItem';
import BurgerMenu from './BurgerMenu';
import ProfileSlideout from './Profile/ProfileSlideout';
import SlideoutMenuPanel from './Slideout/SlideoutMenuPanel';
import useInheritedTheme from '@cb/solaris-react/Hooks/UseInheritedTheme';
import HelpSlideout from './Help/HelpSlideout';
const OverlayOpacityTransitionTime = 200;
const FlyoutPositionTransitionTime = 200;
var FlyoutMode;
(function (FlyoutMode) {
    FlyoutMode[FlyoutMode["PROFILE"] = 0] = "PROFILE";
    // PRODUCT_SWITCHER,
    FlyoutMode[FlyoutMode["HELP"] = 1] = "HELP";
    FlyoutMode[FlyoutMode["CUSTOM"] = 2] = "CUSTOM";
})(FlyoutMode || (FlyoutMode = {}));
export default function SideMenu(props) {
    const [flyoutState, setFlyoutState] = useState({ mode: FlyoutMode.PROFILE, open: false });
    const [overlayVisible, setOverlayVisible] = useState(false);
    const [overlayOpacity, setOverlayOpacity] = useState(0);
    const hideOverlay = useCallback(() => {
        if (!flyoutState.open)
            setOverlayVisible(false);
    }, [flyoutState.open]);
    useEffect(() => {
        let timeout;
        if (flyoutState.open) {
            setOverlayVisible(true);
            timeout = setTimeout(() => setOverlayOpacity(1), 50);
        }
        else {
            setOverlayOpacity(0);
            timeout = setTimeout(hideOverlay, OverlayOpacityTransitionTime + 150);
        }
        return () => {
            if (timeout)
                clearTimeout(timeout);
        };
    }, [flyoutState.open, hideOverlay]);
    const onFlyoutOpenClick = (mode) => {
        setFlyoutState((current) => {
            let open = false;
            if (current.open) {
                if (current.mode === mode) {
                    open = false;
                }
                else {
                    open = true;
                }
            }
            else {
                open = true;
            }
            return {
                mode,
                open,
            };
        });
    };
    const [slideoutPanel, setSlideoutPanel] = useState();
    const closeSlideoutPanel = () => setFlyoutState({ ...flyoutState, open: false });
    const openProfilePanel = () => onFlyoutOpenClick(FlyoutMode.PROFILE);
    const openHelpPanel = () => onFlyoutOpenClick(FlyoutMode.HELP);
    const sideMenuItems = props.sideMenuLinksComponentGenerator
        ? props.sideMenuLinksComponentGenerator((content) => {
            onFlyoutOpenClick(FlyoutMode.CUSTOM);
            setSlideoutPanel(content);
        }, closeSlideoutPanel)
        : undefined;
    let slidoutContent = null;
    switch (flyoutState.mode) {
        case FlyoutMode.PROFILE:
            slidoutContent = _jsx(ProfileSlideout, { close: closeSlideoutPanel });
            break;
        // case FlyoutMode.PRODUCT_SWITCHER:
        // 	slidoutContent = <ProductSwitcherSlideout close={closeSlideoutPanel} />;
        // 	break;
        case FlyoutMode.HELP:
            slidoutContent = _jsx(HelpSlideout, { close: closeSlideoutPanel });
            break;
        case FlyoutMode.CUSTOM:
            slidoutContent = slideoutPanel ? slideoutPanel : null;
            break;
    }
    const theme = useInheritedTheme();
    return (_jsxs(_Fragment, { children: [_jsxs(StyledSideMenu, { "$sideMenuWidth": getVerticalSideMenuWidth(theme), children: [_jsx(BurgerMenu, { items: createBurgerMenuItems(openProfilePanel, props.burgerMenuItems) }), sideMenuItems, _jsxs(StyledBottomButtonsContainer, { children: [props.secondaryBottomItems, _jsx(BottomButtonItem, { iconName: "help", callback: openHelpPanel, tooltip: "Help" }), _jsx(BottomButtonItem, { iconName: "person", callback: openProfilePanel, tooltip: "Your profile" })] })] }), _jsx(FlyoutContainer, { open: flyoutState.open, children: flyoutState.open && _jsx(SlideoutMenuPanel, { content: slidoutContent }) }), overlayVisible && _jsx(Overlay, { opacity: overlayOpacity, onClick: closeSlideoutPanel })] }));
}
const createBurgerMenuItems = (openProfileSlideout, items) => {
    const menuItems = items ?? [];
    const bottomItems = [
        {
            text: 'Profile',
            icon: 'person',
            action: openProfileSlideout,
        },
    ];
    return [...menuItems, ...bottomItems];
};
export function getVerticalSideMenuWidth(theme) {
    return theme.sizing.md;
}
export const StyledItemsContainer = styled.div `
	@media (max-width: ${(props) => props.theme.breakpoints.sm}) {
		display: none;
	}
`;
const StyledSideMenu = styled.div `
	z-index: 3;
	position: sticky;
	top: 0;

	height: 100vh;
	height: 100dvh;
	max-height: fill-available;
	min-width: ${({ $sideMenuWidth }) => $sideMenuWidth};
	background-color: ${(props) => props.theme.primary};

	display: flex;
	flex-direction: column;

	@media (max-width: ${(props) => props.theme.breakpoints.sm}) {
		width: 100vw;
		min-width: unset;
		height: unset;
		flex-direction: row;
		justify-content: space-between;
	}

	.tippy-content .tooltip-body {
		white-space: nowrap;
	}
`;
const StyledBottomButtonsContainer = styled.div `
	margin-top: auto;
	@media (max-width: ${(props) => props.theme.breakpoints.sm}) {
		flex-grow: 1; /* Allow container to fill full width, useful for page titles */
	}
	min-width: ${(props) => props.theme.sizing.md};
	color: ${(props) => props.theme.secondary};
`;
const FlyoutContainer = styled.div `
	position: fixed;
	background: ${(props) => props.theme.primary};
	height: 100%;
	width: ${(props) => props.theme.sizing.xl};
	box-shadow: 0px 0px 20px 0px rgb(0 0 0 / 12%);
	z-index: 2;
	top: 0;
	left: 0;
	transform: translateX(${(props) => (props.open ? '56px' : `-${props.theme.sizing.xl}`)});
	transition: transform ${FlyoutPositionTransitionTime}ms ease-in-out;

	@media (max-width: ${(props) => props.theme.breakpoints.sm}) {
		transform: translateX(${(props) => (props.open ? '0' : `-${props.theme.sizing.xl}`)});
	}
`;
const Overlay = styled.div `
	position: fixed;
	background: #00000030;
	height: 100%;
	width: 100vw;
	z-index: 1;
	top: 0;
	left: 0;
	filter: opacity(${(props) => props.opacity});
	transition: filter ${OverlayOpacityTransitionTime}ms 60ms ease-in-out;
`;
//# sourceMappingURL=SideMenu.js.map